'use client';

import { useTranslations } from 'next-intl';
import { useRef, useState } from 'react';
import { useElementErrorMsg, useFetch } from '@/app/hooks';
import type { ElementType, ImageFormState, InputRefType, TextFormState, VideoFormState } from '@/app/types';
import { reportEvent } from '@/app/utils';
import { materialsCreateElement, materialsCreateElementCheck, materialsUpdateElement } from '../apis';
import type { MaterialsCreateElementCheckData, MaterialsCreateElementData, MaterialV1Element } from '../types';

interface UseElementDialogApiProps {
  state: ImageFormState | VideoFormState | TextFormState;
  initialName?: string;
  isEditMode: boolean;
  onSave: (element: ElementType) => void;
  onStateUpdate?: (updates: Partial<ImageFormState | VideoFormState | TextFormState>) => void;
}

export const useElementDialogApi = ({
  state,
  initialName,
  isEditMode,
  onSave,
  onStateUpdate,
}: UseElementDialogApiProps) => {
  const nameRef = useRef<InputRefType>(null);
  const { showErrorMsg, getErrorMsg, showSuccessMsg } = useElementErrorMsg();
  const [nameCheckMsg, setNameCheckMsg] = useState('');

  const t = useTranslations();

  //创建元素
  const { loading: createLoading, run: createElement } = useFetch(
    (params: Omit<MaterialsCreateElementData, 'url'>) => {
      return materialsCreateElement(params);
    },
    {
      manual: true,
      onSuccess: (data) => {
        if (data.error) {
          showErrorMsg(data.error);
        } else {
          showSuccessMsg({ message: t('elements.actionMsg.createSuccess') });
          onSave({
            ...state,
            ...data.data,
          } as ElementType);
        }
      },
    },
  );

  //更新元素
  const { loading: updateLoading, run: updateElement } = useFetch(materialsUpdateElement, {
    manual: true,
    onSuccess: (data) => {
      if (data.error) {
        showErrorMsg(data.error);
      } else {
        showSuccessMsg();
        onSave({
          ...state,
          ...data.data,
        } as ElementType);
      }
    },
  });

  // 统一的提交函数
  const submit = (data: MaterialV1Element) => {
    if (isEditMode) {
      // 更新模式：需要排除只读字段并构建 mask（如果做了 recaption，返回的数据可能包含一些只读字段）
      const {
        id: _id,
        updated_at: _updated_at,
        created_at: _created_at,
        creator_id: _creator_id,
        version: _version,
        type: _type,
        pay_info: _pay_info,
        ...rest
      } = data;

      updateElement({
        body: {
          element: {
            ...rest,
          },
          mask: Object.getOwnPropertyNames(rest).join(','),
        },
        path: {
          'element.id': data.id as string,
        },
      });
    } else {
      // 创建模式
      createElement({
        body: data,
      });
    }
  };

  // 统一的提交 loading 状态
  const submitLoading = createLoading || updateLoading;

  const checkName = async (): Promise<boolean> => {
    const name = nameRef.current?.getInputValue();
    if (initialName === name) {
      return true;
    }

    reportEvent('element_click', {
      element_name: 'next_step',
    });

    try {
      const params = {
        query: {
          name: name?.trim(),
        },
      };
      const data = await checkElementNameAsync(params);

      if (!data.error) {
        onStateUpdate?.({ name: params.query?.name });
        return true;
      }

      const msg = getErrorMsg(data.error);
      showErrorMsg(data.error);
      setNameCheckMsg(msg);
      return false;
    } catch {
      return false;
    }
  };

  //检查元素名称
  const { loading: checkLoading, runAsync: checkElementNameAsync } = useFetch(
    (params: Omit<MaterialsCreateElementCheckData, 'url'>) => {
      return materialsCreateElementCheck(params);
    },
    {
      manual: true,
      onBefore: () => {
        setNameCheckMsg('');
      },
    },
  );

  return {
    nameRef,
    checkLoading,
    nameCheckMsg,
    setNameCheckMsg,
    checkName,
    submitLoading,
    submit,
  };
};
